import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import DateRange from "components/ui/date-range";
import { useFetch } from "hooks";
import { put } from "utils/api";
import { errorMessage } from "actions/message";
import DateRangeContext from "../date-range-context";
import PatientDemographicLogTable from "./table";

/** First page of logs for a date range, newest first. The response carries the total for the pager. */
export const logsQuery = (startDate, endDate) => ({
  filter_by: [
    { by: "startDate", value: startDate },
    { by: "endDate", value: endDate },
  ],
  sort: { by: "timestamp", direction: "desc" },
  pagination: { page: 1, per: 150 },
  search_for: {},
  search_for_array: [],
});

// The logs are patient PHI and every load is recorded in the access audit, so they are fetched here,
// only while this tab is open (S3DEV2-7684) — never by the company page on mount. Local state via
// useFetch: nothing else in the app reads them, so they do not belong in the redux store.
const PatientDemographicLogs = ({ params: { id } }) => {
  const dispatch = useDispatch();
  const {
    startDate,
    endDate,
    setStartDate,
    setEndDate,
    dateRangeIsValid,
    handleSubmit,
  } = React.useContext(DateRangeContext);

  const [indexParams, setIndexParams] = useState(() => logsQuery(startDate, endDate));
  const { response, isFetching, fetchData } = useFetch({
    apiFn: (query) => put(`companies/${id}/patient-demographic-changes`, query),
    defaultValue: { demographic_changes: [], total: 0 },
    onError: () =>
      dispatch(errorMessage("An error occurred while getting patient demographic logs.")),
  });

  const fetchLogs = (query) => {
    setIndexParams(query);
    fetchData(query);
  };

  useEffect(() => {
    fetchLogs(logsQuery(startDate, endDate));
  }, [id]);

  const submitDateRange = (start, end) => {
    handleSubmit(start, end);
    fetchLogs(logsQuery(start, end));
  };

  const logs = response?.demographic_changes ?? [];

  return (
    <div className="company-demo-logs-container">
      <div className="date-range-input">
        <DateRange
          startDate={startDate}
          endDate={endDate}
          setStartDate={setStartDate}
          setEndDate={setEndDate}
          handleSubmit={submitDateRange}
          isValid={dateRangeIsValid}
        />
      </div>
      <PatientDemographicLogTable
        logs={logs}
        // An older backend returns no total; fall back to the page size.
        total={response?.total ?? logs.length}
        isFetching={isFetching}
        indexParams={indexParams}
        onQueryChange={fetchLogs}
      />
    </div>
  );
};

PatientDemographicLogs.propTypes = {
  params: PropTypes.shape({ id: PropTypes.string.isRequired }),
};

export default PatientDemographicLogs;
